import { useCallback, useEffect, useState } from "react";
import ApiUsersManagement from "../../../../api/user-manager";

// LF360-2038: every project row offers that project's own sites, by that
// project's own sites setting. The forms used to offer the current project's
// first twenty sites in every row, so a site picked under another project was
// refused on save, and that project's real sites could not be named.

const NONE = { sitesEnabled: false, options: [] };

export const toSiteOptions = (sites = []) =>
  sites.map((site) => ({
    label: site.status === "inactive" ? `${site.name} (inactive)` : site.name,
    value: site.id,
    // Kept when a user is already on it, never offered anew.
    disabled: site.status === "inactive",
  }));

/**
 * A row's sites as tags for TagsSelect: what it can pick, and what it holds.
 * An inactive site is listed only while the row already holds it.
 */
export const siteTagsFor = ({ options }, siteIds = []) => {
  const toTag = ({ value, label }) => ({ value, label, key: value });
  const byId = new Map(options.map((option) => [option.value, option]));

  return {
    offered: options
      .filter((option) => !option.disabled || siteIds.includes(option.value))
      .map(toTag),
    selected: siteIds.map((id) => byId.get(id)).filter(Boolean).map(toTag),
  };
};

/**
 * @param {string[]} projectIds the projects the form has rows for
 * @param {boolean} active whether the form is open; closing it forgets the
 *   sites, so the next opening shows sites added since
 * @returns {(projectId: string) => { sitesEnabled: boolean, options: object[] }}
 */
export const useProjectSites = (projectIds, active = true) => {
  const [byProject, setByProject] = useState({});
  const key = [...new Set((projectIds || []).filter(Boolean))].sort().join(",");

  useEffect(() => {
    if (!active) setByProject({});
  }, [active]);

  useEffect(() => {
    if (!active || !key) return undefined;

    const missing = key.split(",").filter((id) => !byProject[id]);
    if (!missing.length) return undefined;

    let cancelled = false;

    ApiUsersManagement.getProjectSites(missing)
      .then(({ data }) => {
        if (cancelled) return;
        setByProject((prev) => {
          const next = { ...prev };
          (data?.projects || []).forEach((project) => {
            next[project.projectId] = {
              sitesEnabled: project.sitesEnabled === true,
              options: toSiteOptions(project.sites),
            };
          });
          return next;
        });
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
    // byProject is read, not watched: a fetch fills it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, active]);

  return useCallback((projectId) => byProject[projectId] || NONE, [byProject]);
};
